/* eslint-disable react-hooks/exhaustive-deps */
import { useTransactionService } from '../../services/transactionService';
import { useEffect, useState } from 'react';
import { Table, Empty } from 'antd';
import { useCredits } from '../../context/CreditsContext';
import { useTranslation } from 'react-i18next';
import { notifications } from '../../common/constants/notifications';
import { creditOperations } from '../../common/constants/creditOperations';
import { datesFormat } from '../../common/constants/datesFormat';
import { TableCellText } from '../../common/components/TableCellText/TableCellText';
import classes from './style.module.scss';
import vinClasses from '../../common/styles/vinHighlighting.module.scss';
import dayjs from 'dayjs';
import globalNotification from '../../common/hooks/globalNotification';
import capitalize from '../../utils/capitalize';
import 'dayjs/locale/uk';
import { usePaginationConfig } from '../../hooks/usePaginationConfig';
import { VIN_CODE_REGEX } from '../../common/constants/vinCode';

export const TransactionsTable = () => {
    const { t, i18n } = useTranslation(['transactions']);
    const { credits } = useCredits();
    const { getTransactions } = useTransactionService();

    const [isLoading, setIsLoading] = useState(false);
    const [transactionsData, setTransactionsData] = useState({
        items: [],
        total: 0,
    });

    const [searchParams, setSearchParams] = useState({
        limit: 5,
        currentPage: 1,
    });

    const getUserTransactions = async () => {
        setIsLoading(true);
        try {
            const offset = (searchParams.currentPage - 1) * searchParams.limit;
            const res = await getTransactions(offset, searchParams.limit);

            setTransactionsData({
                items: res.data.transactions,
                total: res.data.total,
            });
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.TRANSACTIONS,
                action: notifications.action.FETCH,
                plural: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (credits !== undefined && credits !== null) {
            getUserTransactions();
        }
    }, [searchParams, credits]);

    const handleTableChange = pagination => {
        setSearchParams({
            limit: pagination.pageSize,
            currentPage: pagination.current,
        });
    };

    const paginationConfig = usePaginationConfig({
        currentPage: searchParams.currentPage,
        pageSize: searchParams.limit,
        total: transactionsData.total,
        onChange: handleTableChange,
        localStorageKey: 'transactionsTablePagination',
        className: classes['table-wrapper-pagination'],
    });

    const columns = [
        {
            title: '№',
            dataIndex: 'id',
            key: 'id',
            render: id => <TableCellText text={id} />,
            align: 'start',
        },
        {
            title: t('table.credits'),
            dataIndex: 'creditChange',
            key: 'creditChange',
            render: (creditsChange, record) => (
                <TableCellText
                    className={
                        record.creditOperation === creditOperations.REFILL ||
                        record.creditOperation === creditOperations.REFUND
                            ? classes['credits-positive']
                            : classes['credits-negative']
                    }
                    text={
                        record.creditOperation === creditOperations.REFILL ||
                        record.creditOperation === creditOperations.REFUND
                            ? `${creditsChange}`
                            : `-${creditsChange}`
                    }
                />
            ),
            align: 'start',
        },
        {
            title: t('table.product'),
            dataIndex: 'product',
            key: 'product',
            render: (product, record) => {
                const isVinCode = VIN_CODE_REGEX.test(product);
                return (
                    <TableCellText
                        className={isVinCode ? vinClasses.vinHighlight : ''}
                        text={record.creditOperation === creditOperations.REFUND ? t('table.refund') : product}
                    />
                );
            },
            align: 'start',
        },
        {
            title: t('table.cost'),
            dataIndex: 'payment',
            key: 'payment',
            render: payment => <TableCellText text={payment ? `${payment.amountTotal / 100}$` : '---'} />,
            align: 'start',
        },
        {
            title: t('table.date'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: createdAt => (
                <TableCellText
                    text={capitalize(
                        dayjs(createdAt)
                            .locale(i18n.language)
                            .format(i18n.language === 'uk' ? datesFormat.uk.longWithTime : datesFormat.en.longWithTime),
                        i18n.language === 'uk' ? 3 : 0
                    )}
                />
            ),
            align: 'start',
        },
    ];

    return (
        <Table
            dataSource={transactionsData.items}
            columns={columns}
            loading={isLoading}
            rowClassName={classes['transactions-row']}
            className={classes[('transactions-table', 'table-wrapper')]}
            pagination={paginationConfig}
            locale={{ emptyText: <Empty description={t('table.no-transactions')} /> }}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
        />
    );
};
